import {
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import {
  AddCircleOutlineOutlined,
  CalendarMonthOutlined,
  CalendarTodayOutlined,
  FilterListOutlined,
  ListOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { DataGrid } from "../../components/common/Index";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CalendarView from "../../components/common/CalendarView";
import apiService from "../../service/Api";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/Index";
import { toast } from "react-toastify";
import { logout } from "../../store/slice/AuthSlice";
import type { BackendStatus } from "../bookings/types/list-bookings";
import type { FrontendStatus } from "../../types/bookings";
import formatDate from "../../utils/FormatDate";
import { getStatusColor } from "../../utils/GetStatusColor";
import TenDayForecast from "./TenDayForecast";
import { IStreams } from "../../types/streams";

const statusMapToFrontend: Record<BackendStatus, FrontendStatus> = {
  pending: "enquiry",
  confirmed: "confirmed",
  completed: "fulfilled",
  cancelled: "cancelled",
  lost: "lost",
  testing: "testing",
};

interface EventsService {
  venue: {
    name: string;
  };
}

interface StreamResponse {
  id: number;
  booking: {
    id: number;
    status: string;
    events?: Array<{
      id: number;
      start_time: string;
      end_time: string;
      venue: {
        id: number;
        name: string;
        address_one: string;
        address_two: string;
        suburb: string;
        city: string;
      };
      title: string;
      service_notes: string;
      special_request: string | null;
      start_date_of_event: string;
      end_date_of_event: string;
      access_type: string;
      streaming_platform: string;
      session_to_stream: string;
      retention_duration: string;
      provide_on_demand_access: boolean;
      record_stream: boolean;
      require_custom_branding: boolean;
      branding_requirements: string;
      expected_audience_size: number;
    }>;
  };
  service: { venue: { name: string }; church_name: string };
  title: string;
  date: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  start: string;
  end: string;
  status: string;
  vimeo_download_password: string | null;
  vimeo_stream_link: string | null;
  over_due_by: string | null;
  password_protected: boolean | null;
}

const ListStreams = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || []
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<
    "table" | "calendar" | "kanban" | "ten-day-forecast"
  >("table");
  const [streams, setStreams] = useState<IStreams[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "error" | "success";
  }>({ open: false, message: "", severity: "error" });

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [streamToDelete, setStreamToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const hasPermission = (permission: string) => {
    return userPermissions.includes(permission.trim().replace(/'$/, ""));
  };

  const handleStatusChange = async (id: number, newStatus: FrontendStatus) => {
    try {
      const statusMapToBackend: Record<FrontendStatus, string> = {
        enquiry: "pending",
        confirmed: "confirmed",
        fulfilled: "completed",
        cancelled: "cancelled",
        lost: "lost",
        testing: "testing",
      };

      const backendStatus = statusMapToBackend[newStatus] || newStatus;

      await apiService.patch(`/api/streams/stream/${id}/`, {
        status: backendStatus,
      });

      setStreams((prevStreams) =>
        prevStreams.map((stream) =>
          Number(stream.id) === Number(id)
            ? { ...stream, status: newStatus }
            : stream
        )
      );

      toast.success("Stream status updated successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } catch (err: any) {
      toast.error("Failed to update stream status", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      console.error("Stream status update error:", err);
    }
  };

  const fetchStreams = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        search: appliedSearchTerm,
        ordering: "-id",
      };

      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: StreamResponse[];
      }>("/api/streams/stream/", params);

      const validStatuses = [
        "pending",
        "confirmed",
        "completed",
        "cancelled",
        "lost",
        "testing",
      ];

      const mappedStreams = response.data.results.map((stream) => {
        const status: FrontendStatus = validStatuses.includes(
          stream.booking.status
        )
          ? statusMapToFrontend[stream.booking.status as BackendStatus] ||
            "enquiry"
          : "enquiry";

        return {
          id: stream.id.toString(),
          bookingId: `B${stream.booking.id.toString().padStart(3, "0")}`,
          title: stream.title || stream?.service?.church_name || "Untitled",
          clientName: "Unknown",
          serviceDate: formatDate(stream.date),
          dateOfService: stream.date,
          serviceAddress: `${
            stream?.service?.venue?.name ||
            stream?.booking?.events?.[0]?.venue?.name ||
            ""
          },${stream.address_one || ""}, ${stream.address_two || ""}, ${
            stream.suburb || ""
          }, ${stream.city || ""}`
            .replace(/, ,/g, ",")
            .replace(/,,/g, ",")
            .trim(),
          status: stream.status || "",
          originalStatus: status,
          producer: "N/A",
          start: stream.start,
          end: stream.end,
          vimeo_stream_link: stream.vimeo_stream_link || null,
          vimeo_download_password: stream.vimeo_download_password || null, // Add this field
          over_due_by: null, // Set to null or compute if you have logic
          password_protected: !!stream.vimeo_download_password, // Add this field
        };
      });

      setStreams(mappedStreams);
      setRowCount(response.data.count);
      setIsLoading(false);
    } catch (error: any) {
      console.error("Error fetching streams:", error, error.response);
      let errorMessage = "Failed to fetch streams. Please try again.";
      if (error.response?.status === 500) {
        errorMessage = "Server error: Unable to retrieve streams.";
      } else if (error.response?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStreams();
  }, [appliedSearchTerm, dispatch, navigate, paginationModel]);

  // Debounce for search term
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (searchTerm !== appliedSearchTerm) {
      debounceTimer.current = setTimeout(() => {
        setAppliedSearchTerm(searchTerm);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 500);
    }
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchTerm, appliedSearchTerm]);

  const handleSearch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setAppliedSearchTerm(searchTerm);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const confirmDelete = (id: string) => {
    setStreamToDelete(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteStream = async () => {
    if (streamToDelete === null) return;
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/streams/stream/${streamToDelete}/`);
      setStreams((prev) =>
        prev.filter((stream) => stream.id !== streamToDelete)
      );
      toast.success("Stream deleted successfully", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      fetchStreams();
    } catch (err: any) {
      let errorMessage = "Failed to delete stream";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
      console.error("Delete stream error:", err);
    } finally {
      setIsDeleting(false);
      setOpenDeleteDialog(false);
      setStreamToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setStreamToDelete(null);
  };

  const getStatusChipProps = (status: string) => {
    switch (status.toLowerCase()) {
      case "enquiry":
        return {
          label: "Enquiry",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case "confirmed":
        return {
          label: "Confirmed",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "fulfilled":
        return {
          label: "Fulfilled",
          bgcolor: "#0000FF26",
          color: "#0000FF",
        };
      case "cancelled":
        return {
          label: "Cancelled",
          bgcolor: "#EB433540",
          color: "#EB4335",
        };

      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  const columns: GridColDef<IStreams>[] = [
    { field: "id", headerName: "Stream ID", width: 120 },
    { field: "bookingId", headerName: "Booking ID", width: 120 },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params) => {
        const title = params.value || "";
        return title.replace(/^Stream for\s*/i, "");
      },
    },
    { field: "serviceDate", headerName: "Stream Date", flex: 1 },
    {
      field: "serviceAddress",
      headerName: "Service Address",
      flex: 1,
      renderCell: (params) => {
        const [venue, ...addressParts] = (params.value || "").split(",");
        const address = addressParts.join(", ").trim();

        return (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
            {/* Venue on top */}
            <Typography variant="subtitle2" fontWeight="bold">
              {venue || "No Venue"}
            </Typography>

            {/* Address below */}
            <Typography
              variant="body2"
              sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {address}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <FormControl fullWidth>
          <Select
            value={params.row?.status || "enquiry"}
            onChange={(event) =>
              params.row?.id &&
              handleStatusChange(
                Number(params.row.id),
                event.target.value as FrontendStatus
              )
            }
            onClick={(event) => event.stopPropagation()}
            onOpen={(event) => event.stopPropagation()}
            MenuProps={{
              onClick: (event) => event.stopPropagation(),
            }}
            sx={{
              "& .MuiSelect-select": {
                padding: "4px 8px",
                width: "100%",
                color: reduxTheme.neutral_colors?.[100] || "#333",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
          >
            <MenuItem value="Enquiry">
              <Chip
                label="Enquiry"
                sx={{
                  bgcolor: "#F79E1B40",
                  color: "#F79E1B",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="Confirmed">
              <Chip
                label="Confirmed"
                sx={{
                  bgcolor: "#4BA66340",
                  color: "#4BA663",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="Fulfilled">
              <Chip
                label="Fulfilled"
                sx={{
                  bgcolor: "#0000FF26",
                  color: "#0000FF",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
            <MenuItem value="Cancelled">
              <Chip
                label="Cancelled"
                sx={{
                  bgcolor: "#EB433540",
                  color: "#EB4335",
                  width: "100%",
                  "& .MuiChip-label": { fontWeight: 500 },
                }}
              />
            </MenuItem>
          </Select>
        </FormControl>
      ),
    },

    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {/* {hasPermission("stream.view_stream") && (
            <IconButton
              aria-label="view"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/stream-management/view/${params.row.id}`);
              }}
              sx={{
                color: reduxTheme.primary_colors?.[400] || "#614438",
                "&:hover": {
                  backgroundColor:
                    reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                },
              }}
            >
              <VisibilityOutlined />
            </IconButton>
          )} */}
          {hasPermission("stream.change_stream") && (
            <IconButton
              aria-label="edit"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/stream-management/edit/${params.row.id}`);
              }}
              sx={{
                color: reduxTheme.primary_colors?.[400] || "#614438",
                "&:hover": {
                  backgroundColor:
                    reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                },
              }}
            >
              <EditOutlined />
            </IconButton>
          )}
          {hasPermission("stream.delete_stream") && (
            <IconButton
              aria-label="delete"
              onClick={(event) => {
                event.stopPropagation();
                confirmDelete(params.row.id);
              }}
              sx={{
                color: "#EB4335",
                "&:hover": {
                  backgroundColor:
                    reduxTheme.neutral_colors?.[800] || "#f5f5f5",
                },
              }}
            >
              <DeleteOutline />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleRowClick = (params: GridRowParams<IStreams>) => {
    navigate(`/stream-management/view/${params.row.id}`);
  };

  return (
    <Box>
      <Box
        className="print-header"
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            backgroundColor: reduxTheme.primary_colors[900],
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            width: { xs: "100%", md: "30%" },
          }}
        >
          <IconButton onClick={handleSearch}>
            <SearchOutlined />
          </IconButton>

          <InputBase
            placeholder="Search by title, service, or package..."
            sx={{ ml: 1, flex: 1 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />

          <IconButton>
            <FilterListOutlined />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,

            flexWrap: { xs: "wrap", md: "nowrap" },
            justifyContent: { xs: "center", md: "flex-end" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Button
            startIcon={<CalendarTodayOutlined />}
            onClick={() => navigate("/stream-management/ten-day-forecast")}
            sx={{
              color: reduxTheme.primary_colors[100],
              backgroundColor:
                viewMode === "ten-day-forecast"
                  ? reduxTheme.primary_colors[500]
                  : "transparent",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors[500],
                color: reduxTheme.secondary_colors[400],
              },

              minWidth: { xs: "45%", md: "auto" },
            }}
          >
            10 Day Forecast
          </Button>

          <Button
            startIcon={<ListOutlined />}
            onClick={() => setViewMode("table")}
            sx={{
              color: reduxTheme.primary_colors[100],
              backgroundColor:
                viewMode === "table"
                  ? reduxTheme.primary_colors[500]
                  : "transparent",
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors[500],
                color: reduxTheme.secondary_colors[400],
              },
              minWidth: { xs: "45%", md: "auto" },
            }}
          >
            Table
          </Button>

          <Button
            startIcon={<CalendarMonthOutlined />}
            onClick={() => setViewMode("calendar")}
            sx={{
              color: reduxTheme.primary_colors[100],
              backgroundColor:
                viewMode === "calendar"
                  ? reduxTheme.primary_colors[500]
                  : reduxTheme.primary_colors[800],
              "&:hover": {
                backgroundColor: reduxTheme.primary_colors[500],
                color: reduxTheme.secondary_colors[400],
              },
              minWidth: { xs: "45%", md: "auto" },
            }}
          >
            Calendar
          </Button>

          {hasPermission("stream.add_stream") && (
            <Button
              startIcon={<AddCircleOutlineOutlined />}
              onClick={() => navigate("/stream-management/add")}
              variant="contained"
              sx={{
                backgroundColor: reduxTheme.secondary_colors[400],
                "&:hover": {
                  backgroundColor: reduxTheme.primary_colors[500],
                },
                minWidth: { xs: "45%", md: "auto" },
              }}
            >
              Add Stream
            </Button>
          )}
        </Box>
      </Box>

      {viewMode === "table" && (
        <>
          <DataGrid
            rows={streams}
            columns={columns}
            loading={isLoading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={handleRowClick}
            rowHeight={60}
          />
        </>
      )}

      {viewMode === "calendar" && (
        <Box sx={{ minHeight: "60vh" }}>
          <CalendarView
            bookings={streams}
            renderChip={(stream: IStreams) => {
              const title = stream.title;

              // Map backend status to frontend status
              const validStatuses = [
                "pending",
                "confirmed",
                "fulfilled",
                "completed",
                "cancelled",
                "lost",
                "testing",
              ];
              const mappedStatus: FrontendStatus = validStatuses.includes(
                stream?.status || ""
              )
                ? statusMapToFrontend[stream?.status as BackendStatus] ||
                  "fulfilled"
                : "enquiry";

              return (
                <Chip
                  label={title}
                  size="small"
                  onClick={() =>
                    navigate(`/stream-management/view/${stream.id}`)
                  }
                  sx={{
                    backgroundColor: getStatusColor(mappedStatus).bg,
                    color: getStatusColor(mappedStatus).color,
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                />
              );
            }}
          />
        </Box>
      )}
      {viewMode === "ten-day-forecast" && <TenDayForecast />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this stream? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{ color: reduxTheme.neutral_colors?.[100] }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteStream}
            sx={{ color: "#EB4335" }}
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CircularProgress size={24} sx={{ color: "#EB4335" }} />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListStreams;
